import React, { useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useScanner from '@afosto/utils-react/input/useScanner';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { useAppState } from '@afosto/app-components-react/AppProvider';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import FulfilmentLocationFilter from '../../components/FulfilmentLocationFilter';
import FulfilmentLocationRenderer from '../../components/FulfilmentLocationRenderer';
import ParcelCard, { ParcelCardSkeleton } from './ParcelCard';
import ShipmentFilter from '../../components/ShipmentFilter';
import ShipmentRenderer from '../../components/ShipmentRenderer';
import SwitchLocationsDialog from '../../components/SwitchLocationsDialog';
import BulkButton from '@afosto/components-react/BulkButton';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import Page from '@afosto/app-components-react/Page';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import createDefaultView from '@afosto/app-components-react/DataSource/modules/views/utils';
import filterOptionsQuery from '@afosto/utils-react/datagrid/filterOptionsQuery';
import updateDataGridDefaultView from '@afosto/utils-react/datagrid/updateDataGridDefaultView';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import searchQuery from '@afosto/utils-react/datagrid/searchQuery';
import wait from '@afosto/utils/wait';
import { parcelFormatter, positionFormatter, shipmentFormatter } from './formatters';
import { fulfilmentLocationFormatter } from '../../utils/formatters';
import { index as searchApi } from '@afosto/content/api/Index';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import translations from './translations';

const PARCELS_PROFILE_CODE = 'lcs';
const PARCELS_PROFILE_MODEL = 'parcels';
const PARCELS_PROFILE_PATH = 'lcs.handling.parcels';
const PARCELS_PROFILE_NAMESPACE = 'default';

const DataListGridContainer = props => <Grid container spacing={1} {...props} />;

const Parcels = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:handling:read',
    'lcs:locations:read',
  ]);

  const history = useHistory();
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedItems, setSelectedItems] = useState([]);
  const [showSwitchLocationsDialog, setShowSwitchLocationsDialog] = useState(false);
  const { settings } = useAppState();
  const { locations = [] } = settings;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const handleScanInput = async inputValue => {
    try {
      if (!inputValue) {
        return;
      }

      const response = await searchApi.search({
        codes: ['lcs'],
        models: ['parcels'],
        namespaces: ['default'],
        q: [inputValue],
        is_fuzzy: false,
      });
      const { data } = response;
      const [firstHit] = data?.hits ?? [];

      if (firstHit?.reference) {
        history.push(`/parcels/${firstHit?.reference}`);
        return;
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotFound), {
        variant: 'info',
      });
    } catch (err) {
      if (inputValue) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotFound), {
          variant: 'info',
        });
      }
    }
  };

  useScanner(handleScanInput, { disabled: !isMobile || showSwitchLocationsDialog });

  const searchIdentifier = {
    code: PARCELS_PROFILE_CODE,
    model: PARCELS_PROFILE_MODEL,
    namespace: PARCELS_PROFILE_NAMESPACE,
  };
  const fixedProperties = [
    {
      key: 'number',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.number`,
        defaultMessage: 'Parcel number',
        description: 'The column name for column parcel number',
      }),
      type: 'parcel',
    },
  ];
  const defaultProperties = [
    {
      key: 'tracking_code',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.trackingCode`,
        defaultMessage: 'Track & trace code',
        description: 'The column name for column parcel tracking code',
      }),
    },
    {
      key: 'shipment_id',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.shipment`,
        defaultMessage: 'Shipment',
        description: 'The column name for column parcel shipment',
      }),
      type: 'shipment',
      sortKey: 'shipment_number',
    },
    {
      key: 'is_in_transit',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.isInTransit`,
        defaultMessage: 'In transit',
        description: 'The column name for column parcel is in transit',
      }),
      type: 'boolean',
      isSortable: false,
    },
    {
      key: 'is_finished',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.isFinished`,
        defaultMessage: 'Finished',
        description: 'The column name for column parcel is finished',
      }),
      type: 'boolean',
      isSortable: false,
    },
    {
      key: 'is_labeled',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.isLabeled`,
        defaultMessage: 'Labeled',
        description: 'The column name for column parcel is labeled',
      }),
      type: 'boolean',
      isSortable: false,
    },
    {
      key: 'position',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.position`,
        defaultMessage: 'Position',
        description: 'The column name for column parcel position',
      }),
      type: 'position',
    },
    {
      key: 'ship_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.shipAt`,
        defaultMessage: 'Ship at',
        description: 'The column name for column parcel ship at',
      }),
      type: 'datetime',
    },
    {
      key: 'created_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${PARCELS_PROFILE_MODEL}.createdAt`,
        defaultMessage: 'Created at',
        description: 'The column name for column parcel created at',
      }),
      type: 'datetime',
    },
  ];
  const { isFetchingProfile, filters, views, properties: filterProperties } = useDataSourceProfile({
    ...searchIdentifier,
    profilePath: PARCELS_PROFILE_PATH,
    filterTypes: {
      location: 'fulfilmentLocation',
      skus: 'sku',
      shipment_id: 'shipment',
    },
    ignoreFilters: ['indexed_at', 'reference', 'shipment_number'],
    ignoreProperties: ['shipment_number', ...defaultProperties.map(property => property.key)],
    ignoreSorting: ['array', 'boolean', 'fulfilmentLocation', 'location', 'sku', 'shipment_id'],
    propertyTypes: {
      location: 'location',
      skus: 'array',
    },
  });

  const dataSource = useDataGrid(searchQuery(searchIdentifier), {
    gridOptions: {
      columnSelection: true,
      copyGridUrl: true,
      filterSelection: true,
      advancedSort: true,
      viewsSelection: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isFetchingProfile,
      storeStateKey: 'parcels',
    },
    modules: ['dataProperties', 'pagination', 'filters', 'selection', 'sorting', 'views', 'search'],
    dataProperties: {
      properties: [...defaultProperties, ...filterProperties, ...fixedProperties],
      fixedProperties,
    },
    views: {
      storeViewKey: 'parcels_view',
      viewShortcuts: true,
      canCreateViewForTenant:
        sessionManager && sessionManager.hasPermissions(['lcs:handling:write']),
      defaultView: createDefaultView(intl, {
        properties: [...defaultProperties],
        filters: [
          {
            key: 'is_labeled',
            operator: 'in',
            value: [false],
            type: 'boolean',
            isSortable: false,
          },
          {
            key: 'is_outbound',
            operator: 'in',
            value: [true],
            type: 'boolean',
          },
          {
            key: 'is_finished',
            operator: 'in',
            value: [false],
            type: 'boolean',
            isSortable: false,
          },
        ],
        sort: [
          {
            key: 'ship_at',
            direction: 'asc',
          },
        ],
      }),
      views,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(PARCELS_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(PARCELS_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(PARCELS_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(PARCELS_PROFILE_PATH)(repositionedViews),
      onChangeDefaultView: updatedViews =>
        updateDataGridDefaultView(PARCELS_PROFILE_PATH)(updatedViews),
    },
    filters: {
      loadFilterOptions: filterOptionsQuery(searchIdentifier),
      filters,
      fixedFilters: [
        ...(locations?.length
          ? [{ key: 'location', operator: 'in', value: locations.map(locationId => locationId) }]
          : []),
      ],
    },
    selection: {
      allowSelectAll: false,
      onAddSelection: selected => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selected.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selected => {
        const removedIds = selected.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });

  const renderParcel = (parcel, { SelectionComponent }) => (
    <Grid key={parcel.id} item xs={12}>
      <ParcelCard parcel={parcel} SelectionComponent={SelectionComponent} />
      <Divider />
    </Grid>
  );

  const renderParcelSkeleton = key => (
    <Grid key={key} xs={12} item>
      <ParcelCardSkeleton />
      <Divider />
    </Grid>
  );

  const handleMarkInTransit = data => async () => {
    try {
      const { selection: selectionModule } = data;
      const { setSelectedItems: setDataSourceSelectedItems } = selectionModule;

      const parcels = selectedItems.filter(parcel => !parcel.is_in_transit && !parcel.is_finished);
      const transitParcelPromises = parcels.map(parcel => parcelsApi.setParcelInTransit(parcel.id));

      await Promise.all(transitParcelPromises).then(result =>
        result.filter(promise => promise.status === 'fulfilled'),
      );

      const parcelsDataPromises = parcels.map(parcel => parcelsApi.getParcel(parcel.id));
      const parcelsData = await Promise.all(parcelsDataPromises).then(parcelResponses =>
        parcelResponses.map(parcelResponse => parcelResponse?.data),
      );

      const resetParcelPositionsPromises = parcelsData
        .filter(parcel => parcel.position)
        .map(parcel =>
          parcelsApi.updateParcel(parcel.id, {
            weight: parcel?.weight,
            length: parcel?.length,
            width: parcel?.width,
            height: parcel?.height,
            tracking_code: parcel?.tracking_code,
            tracking_url: parcel?.tracking_url,
            ship_at: parcel?.ship_at,
            position: null,
          }),
        );

      await Promise.all(resetParcelPositionsPromises);
      await wait(600);

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelsMarkedInTransit), {
        variant: 'success',
      });

      setDataSourceSelectedItems([]);
      setSelectedItems([]);

      dataSource.reloadDataSource();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelsNotMarkedInTransit), {
        variant: 'error',
      });
    }
  };

  const handleMarkFinished = data => async () => {
    try {
      const { selection: selectionModule } = data;
      const { setSelectedItems: setDataSourceSelectedItems } = selectionModule;

      const parcels = selectedItems.filter(parcel => !parcel.is_finished);
      const finishedParcelPromises = parcels.map(parcel => parcelsApi.setParcelFinished(parcel.id));

      await Promise.allSettled(finishedParcelPromises).then(result =>
        result.filter(promise => promise.status === 'fulfilled'),
      );

      const parcelsDataPromises = parcels.map(parcel => parcelsApi.getParcel(parcel.id));
      const parcelsData = await Promise.all(parcelsDataPromises).then(parcelResponses =>
        parcelResponses.map(parcelResponse => parcelResponse?.data),
      );

      const resetParcelPositionsPromises = parcelsData
        .filter(parcel => parcel.position)
        .map(parcel =>
          parcelsApi.updateParcel(parcel.id, {
            weight: parcel?.weight,
            length: parcel?.length,
            width: parcel?.width,
            height: parcel?.height,
            tracking_code: parcel?.tracking_code,
            tracking_url: parcel?.tracking_url,
            ship_at: parcel?.ship_at,
            position: null,
          }),
        );

      await Promise.all(resetParcelPositionsPromises);
      await wait(600);

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelsMarkedFinished), {
        variant: 'success',
      });

      setDataSourceSelectedItems([]);
      setSelectedItems([]);

      dataSource.reloadDataSource();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelsMarkedNotFinished), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  const bulkActions = [
    {
      key: 'markInTransit',
      render: data => (
        <BulkButton onClick={handleMarkInTransit(data)} key="markInTransit">
          <Typography variant="button">
            <FormattedMessage {...translations.bulkActions.markInTransit} />
          </Typography>
        </BulkButton>
      ),
    },
    {
      key: 'markFinished',
      render: data => (
        <BulkButton onClick={handleMarkFinished(data)} key="markFinished">
          <Typography variant="button">
            <FormattedMessage {...translations.bulkActions.markFinished} />
          </Typography>
        </BulkButton>
      ),
    },
  ];

  const filterFormatters = {
    fulfilmentLocation: FulfilmentLocationFilter,
    shipment: ShipmentFilter,
  };

  const filterRenderers = {
    fulfilmentLocation: FulfilmentLocationRenderer,
    shipment: ShipmentRenderer,
  };

  return (
    <>
      <Page
        title={intl.formatMessage(translations.title)}
        secondaryActions={[
          {
            text: intl.formatMessage(translations.secondaryActions.switchLocations),
            action: () => {
              setShowSwitchLocationsDialog(true);
            },
            shortCut: true,
            icon: ['fal', 'exchange'],
          },
        ]}
        fullWidth
      >
        <Hidden implementation="js" mdUp>
          <DataList
            dataSource={dataSource}
            renderItem={renderParcel}
            renderSkeletonItem={renderParcelSkeleton}
            component={DataListGridContainer}
            bulkActions={bulkActions}
            filterFormatters={filterFormatters}
            filterRenderers={filterRenderers}
          />
        </Hidden>
        <Hidden implementation="js" smDown>
          <DataGrid
            dataSource={dataSource}
            formatters={{
              parcel: parcelFormatter,
              shipment: shipmentFormatter,
              location: fulfilmentLocationFormatter,
              position: positionFormatter,
            }}
            bulkActions={bulkActions}
            filterFormatters={filterFormatters}
            filterRenderers={filterRenderers}
          />
        </Hidden>
      </Page>
      <SwitchLocationsDialog
        isOpen={showSwitchLocationsDialog}
        onCancel={() => setShowSwitchLocationsDialog(false)}
        onSwitch={() => setShowSwitchLocationsDialog(false)}
      />
    </>
  );
};

export default Parcels;
